import { useMemo } from 'react';
import FormModal from '../../Modals/FormModal';
import ImportRecordTable from '../../../components/Tables/import/ImportRecordTable';
import AddImportProductForm from './AddImportProductForm';
import EditImportProductForm from './EditImportProductForm';
import ImportDocumentInfoCard from '../common/ImportDocumentInfoCard';
import { useImportPurchase } from '../../../hooks/purchases/useImportPurchase';
import { ImportProduct } from '../../../types/purchases/BasePurchase';
import ImportPurchaseFinalizeSection from './ImportPurchaseFinalizeSection';
import { useBasePurchase } from '../../../hooks/purchases/useBasePurchase';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import {
  closeAddProductModal,
  closeEditProductModal,
  openAddProductModal,
  openEditProductModal,
  setProductToEdit,
} from '../../../store/slices/purchases/importPurchaseSlice';
import { importProductColumns } from '../../../features/purchases/importation/columns/importProductColumns';
import { useProductSettlement } from '../../../hooks/settlement/useProductSettlement';
import ProductSettlementModal from '../settlement/ProductSettlementModal';
import GeneralProductSettlementModal from '../settlement/GeneralProductSettlementModal';
import SvgIcon from '../../SvgIcon/SvgIcon';

const ImportPurchaseSection = () => {
  const { form } = useSelector((state: RootState) => state.basePurchase);

  const dispatch = useDispatch();
  const { handlerDeleteProduct, handlerGetProductById } = useImportPurchase();
  const { updateField, formErrors } = useBasePurchase();

  const {
    handlerGetSettlementsByProduct,
    handlerOpenGeneralModal,
    handlerCloseGeneralModal,
    handlerCloseSettlementModal,
    showGeneralSettlementModal,
    showSettlementModal,
  } = useProductSettlement();

  const { purchaseSettlementLoaded } = useSelector(
    (state: RootState) => state.basePurchase
  );

  const { showAddProductModal, showEditProductModal, productToEdit } =
    useSelector((state: RootState) => state.importPurchase);

  // ✅ Filtrado con type guard para productos de importación
  const importProducts = useMemo(
    () =>
      form.products.filter(
        (p): p is ImportProduct => 'freight' in p && 'initialCost' in p
      ),
    [form.products]
  );

  const columns = useMemo(() => importProductColumns, []);

  const actions = useMemo(() => {
    if (purchaseSettlementLoaded) {
      return [
        {
          key: 'settle',
          onClick: (row: ImportProduct) => {
            handlerGetSettlementsByProduct(form.id, row.id);
          },
        },
      ];
    }

    return [
      {
        key: 'edit',
        onClick: async (row: ImportProduct) => {
          const product = await handlerGetProductById(row.id);
          if (product) {
            dispatch(setProductToEdit(product));
            dispatch(openEditProductModal());
          }
        },
      },
      {
        key: 'delete',
        onClick: (row: ImportProduct) => {
          handlerDeleteProduct(row.id);
        },
      },
    ];
  }, [purchaseSettlementLoaded, dispatch, handlerGetProductById]);

  return (
    <div className="mt-6">
      <ImportDocumentInfoCard
        form={form}
        updateField={updateField}
        formErrors={formErrors}
      />

      <ImportRecordTable
        key={importProducts.length}
        columns={columns}
        data={importProducts}
        actions={actions}
        noDataMessage="No hay productos de importación agregados."
        tableHeaderContent={
          <>
            <h3 className="text-lg font-medium">Productos de Importación</h3>
            {purchaseSettlementLoaded ? (
              <button
                onClick={handlerOpenGeneralModal}
                className="flex items-center gap-2 rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
              >
                <SvgIcon name="settle-products" />
                Asentar General
              </button>
            ) : (
              <button
                onClick={() => dispatch(openAddProductModal())}
                className="rounded bg-primary px-4 py-2 text-white hover:bg-opacity-90"
              >
                Agregar Producto
              </button>
            )}
          </>
        }
      />

      <FormModal
        key="add-modal"
        isOpen={showAddProductModal}
        onClose={() => dispatch(closeAddProductModal())}
        title="Agregar Producto de Importación"
        maxWidth="max-w-4xl"
      >
        <AddImportProductForm />
      </FormModal>

      <FormModal
        key="edit-modal"
        isOpen={showEditProductModal}
        onClose={() => dispatch(closeEditProductModal())}
        title="Editar Producto de Importación"
        maxWidth="max-w-4xl"
      >
        {productToEdit && (
          <EditImportProductForm
            onClose={() => dispatch(closeEditProductModal())}
          />
        )}
      </FormModal>

      <ImportPurchaseFinalizeSection />

      <ProductSettlementModal
        isOpen={showSettlementModal}
        onClose={handlerCloseSettlementModal}
      />

      <GeneralProductSettlementModal
        isOpen={showGeneralSettlementModal}
        onClose={handlerCloseGeneralModal}
      />
    </div>
  );
};

export default ImportPurchaseSection;
